import { useState } from "react";
import { AnimatedPage } from "../../../../components/AnimatedPage";
import { DocumentTableSkeleton } from "../../../../components/ui/skeleton-loaders";
import { config } from "../../../../utils/config";
import { useToast } from "../../../../hooks/use-toast";
import { DocumentApprovalTable } from "./components/document-approval-table";
import { DocumentDetailDialog } from "./components/document-detail-dialog";
import { SendToAzumiButton } from "./components/send-to-azumi-button";
import { useDocumentApproval } from "./hooks/use-document-approval";
import { DocumentForApproval } from "./types";

export default function DocumentApproval() {
  const fundName = `xama_${config.stage}`;
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentForApproval | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const {
    documents,
    isLoading,
    approveOrRejectDocument,
    rejectDocument,
    sendToAzumi,
    isLoadingAction,
    isRejecting,
    isSendingToAzumi,
  } = useDocumentApproval({
    fundName,
    enabled: !!fundName,
  });

  const handleView = (document: DocumentForApproval) => {
    setSelectedDocument(document);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedDocument(null);
  };

  const handleApprove = (document: DocumentForApproval) => {
    approveOrRejectDocument(document, {
      onSuccess: () => {
        toast({
          title: "Documento aprovado",
          description: "O documento foi aprovado com sucesso.",
        });
        handleCloseDialog();
      },
      onError: () => {
        toast({
          title: "Erro ao aprovar",
          description: "Ocorreu um erro ao aprovar o documento.",
          variant: "destructive",
        });
      },
    });
  };

  const handleSendToAzumi = () => {
    sendToAzumi(undefined, {
      onSuccess: () => {
        toast({
          title: "Cadastros enviados",
          description: "Os cadastros foram enviados para a Azumi com sucesso.",
        });
      },
      onError: () => {
        toast({
          title: "Erro ao enviar para Azumi",
          description: "Ocorreu um erro ao enviar os cadastros para a Azumi.",
          variant: "destructive",
        });
      },
    });
  };

  const handleReject = (document: DocumentForApproval) => {
    rejectDocument(document, {
      onSuccess: () => {
        toast({
          title: "Documento rejeitado",
          description: "O documento foi rejeitado com sucesso.",
        });
        handleCloseDialog();
      },
      onError: () => {
        toast({
          title: "Erro ao rejeitar",
          description: "Ocorreu um erro ao rejeitar o documento.",
          variant: "destructive",
        });
      },
    });
  };

  return (
    <AnimatedPage className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Aprovação de Documentos
            </h1>
            <p className="text-muted-foreground mt-1">
              Aprovar documentos dos cedentes
            </p>
          </div>
          <SendToAzumiButton
            onConfirm={handleSendToAzumi}
            disabled={isSendingToAzumi}
          />
        </div>
      </div>

      {isLoading ? (
        <DocumentTableSkeleton />
      ) : (
        <DocumentApprovalTable documents={documents} onView={handleView} />
      )}

      <DocumentDetailDialog
        document={selectedDocument}
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onApprove={handleApprove}
        onReject={handleReject}
        isApproving={isLoadingAction}
        isRejecting={isRejecting}
      />
    </AnimatedPage>
  );
}
